/**
 * useAuth — global auth-state singleton.
 *
 * Login mit username + password (1.x-kompatibel, bcrypt-verified).
 * Magic-Link-Flow ist NICHT für CMS-Admin-Login — der bleibt reserviert
 * für Staging-Customer-Previews (Phase 5+).
 */
import { ref, readonly } from 'vue'
import { api, ApiError } from '@/lib/api'
import { useSprache } from '@/composables/useSprache'
import { useKundenRechte } from '@/composables/useKundenRechte'
import type { AuthUser } from '@/types/api'

const user = ref<AuthUser | null>(null)
const ready = ref(false)
const loading = ref(false)
const lastError = ref<string | null>(null)

let bootstrapPromise: Promise<void> | null = null

async function bootstrap(): Promise<void> {
  if (bootstrapPromise) return bootstrapPromise
  bootstrapPromise = (async () => {
    try {
      const res = await api.authMe()
      if (res.authenticated) {
        setzeZugang(res.user)
      } else {
        user.value = null
      }
    } catch (e) {
      user.value = null
      if (!(e instanceof ApiError && e.status === 401)) {
        lastError.value = e instanceof Error ? e.message : String(e)
      }
    } finally {
      ready.value = true
    }
  })()
  return bootstrapPromise
}

/**
 * Ein Zugang ist angemeldet.
 *
 * Was ein Kundenzugang in seinem Projekt darf, merkt sich das Panel für die
 * ganze Sitzung (useKundenRechte). Dieser Merkzettel gehört zum Zugang, nicht
 * zum Tab: Ohne das Zurücksetzen hier sah eine Kundin nach dem nächsten
 * Anmelden im selben Tab noch die Rechte von vorher. Bei entzogenem Recht
 * stand „Bearbeiten" weiter da und jedes Speichern endete mit HTTP 403,
 * ein neu vergebenes Recht fehlte bis zum Neuladen (gefunden 02.10.2026).
 */
function setzeZugang(neu: AuthUser): void {
  useKundenRechte().zuruecksetzen()
  user.value = neu
  /* Die Sprache gehoert zum Zugang, nicht zum Browser: Ein Zugang von
     UnderWave bekommt Englisch, ohne dass es jemand einstellt. */
  useSprache().uebernehmeVomServer(neu.sprache)
}

type LoginResult =
  | { ok: true }
  | { ok: false; needsTotp: true; pendingToken: string }
  | { ok: false; reason: string; retryAfter?: number }

async function login(
  username: string,
  password: string,
): Promise<LoginResult> {
  loading.value = true
  lastError.value = null
  try {
    const res = await api.authLogin(username, password)
    if ('authenticated' in res && res.authenticated) {
      setzeZugang(res.user)
      return { ok: true }
    }
    if ('needs_totp' in res && res.needs_totp) {
      return { ok: false, needsTotp: true, pendingToken: res.pending_token }
    }
    // res ist { authenticated: false, reason, retry_after? }. Den Satz dazu
    // schreibt die Anmeldemaske in der Sprache des Lesers.
    const failRes = res as { reason: string; retry_after?: number }
    return {
      ok: false,
      reason: failRes.reason ?? 'unknown',
      retryAfter: failRes.retry_after,
    }
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e)
    lastError.value = msg
    return { ok: false, reason: msg }
  } finally {
    loading.value = false
  }
}

async function completeTotp(
  pendingToken: string,
  code: string,
): Promise<{ ok: true } | { ok: false; reason: string; retryAfter?: number }> {
  loading.value = true
  lastError.value = null
  try {
    const res = await api.authLoginTotp(pendingToken, code)
    if (res.authenticated) {
      setzeZugang(res.user)
      return { ok: true }
    }
    return { ok: false, reason: res.reason, retryAfter: res.retry_after }
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e)
    lastError.value = msg
    return { ok: false, reason: msg }
  } finally {
    loading.value = false
  }
}

async function logout(): Promise<void> {
  loading.value = true
  try {
    await api.authLogout()
  } catch {
    // ignore — we clear local state regardless
  } finally {
    user.value = null
    useKundenRechte().zuruecksetzen()
    loading.value = false
    bootstrapPromise = null
  }
}

async function refresh(): Promise<void> {
  bootstrapPromise = null
  await bootstrap()
}

export function useAuth() {
  return {
    user: readonly(user),
    ready: readonly(ready),
    loading: readonly(loading),
    lastError: readonly(lastError),
    bootstrap,
    refresh,
    login,
    completeTotp,
    logout,
  }
}
